import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Flex,
  Grid,
  Stack,
  Text,
  useBreakpointValue,
  useTheme,
} from '@chakra-ui/react'
import { useGoogleLogin } from '@react-oauth/google'
import { addHours } from 'date-fns'
import { useState } from 'react'

import CardAction from 'components/card-action'
import CardInfo from 'components/card-info'
import DashboardMetrics from 'components/dashboard-metrics'
import Heading from 'components/heading'
import Link from 'components/link'
import Loading from 'components/loading'
import StreamCard from 'components/match-stream-cards/stream-card'
import NotificationHero from 'components/notification-hero'
import YoutubeConnector from 'components/youtube/youtube-connector'
import { useUserContext } from 'contexts/user-context'
import { ProviderType } from 'types'
import { isStreamingPassesActive } from 'utils/customer-utils'
import { getMatchesForStreams } from 'utils/match-utils'
import { isFetched } from 'utils/query-utils'
import { getStreamMatchPairs } from 'utils/stream-utils'

import {
  useGetCustomer,
  useGetCustomerMatches,
  useGetCustomerStreams,
  useGetCustomerYoutubeAccount,
  useGetCustomerYoutubeStatus,
  useUpdateAuthConfig,
  useDelAuthConfig,
} from 'api'
import { FUTURE_HOURS_IN_DASHBOARD, HELP_REQUEST_LINK, STARTING_DATE } from 'settings'

export default function Dashboard() {
  const theme = useTheme()
  const isLargeViewport = useBreakpointValue({ base: false, lg: true })

  const { selectedCustomer, customers, user } = useUserContext()
  const [customLoading, setCustomLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const today = addHours(STARTING_DATE, FUTURE_HOURS_IN_DASHBOARD * -1)
  const todayPlusDays = addHours(STARTING_DATE, FUTURE_HOURS_IN_DASHBOARD)

  const { data: customer, isFetching: isFetchingCustomer } = useGetCustomer(selectedCustomer?.customerGuid ?? '')

  const queryPayload = {
    customerId: selectedCustomer?.customerGuid ?? '',
    startDate: today.toISOString(),
    endDate: todayPlusDays.toISOString(),
    alt: 'dashboard',
  }

  const streamsResult = useGetCustomerStreams(queryPayload)
  const { data: streams = [] } = streamsResult

  const {
    data: matches = [],
    isFetching: isFetchingMatches,
    error: matchesError,
  } = useGetCustomerMatches({
    ...queryPayload,
    includeCustomerIds: false,
  })
  const matchesForStreams = getMatchesForStreams({ streams, matches, startDate: today, endDate: todayPlusDays })
  const streamMatchPairs = getStreamMatchPairs(streams, matches).filter(pair =>
    matchesForStreams.map(m => m.id).includes(pair.match?.id ?? '')
  )

  const { data: customerYoutubeAccount, isFetching: isFetchingYtAccount } = useGetCustomerYoutubeAccount(
    selectedCustomer?.customerGuid
  )

  const { mutateAsync: updateAuthConfig } = useUpdateAuthConfig(
    selectedCustomer?.customerGuid ?? '',
    ProviderType.YOUTUBE
  )
  const { mutateAsync: delAuthConfig } = useDelAuthConfig(selectedCustomer?.customerGuid ?? '', ProviderType.YOUTUBE)
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/youtube.force-ssl',
    onSuccess: async ({ code }) => {
      setCustomLoading(true)
      const cusProviderResponse = await updateAuthConfig({ auth_code: code })
      setCustomLoading(false)
      if (cusProviderResponse?.httpResponseCode === 200) {
        //success
        window.location.reload()
      } else {
        //error
        setErrorMessage(cusProviderResponse.outputMessage)
      }
    },
    onError: ({ error }) => {
      console.error('Google Login Failed', error) // eslint-disable-line no-console
    },
  })

  const googleLogout = async () => {
    setCustomLoading(true)
    try {
      const cusProviderResponse = await delAuthConfig()
      if (cusProviderResponse?.httpResponseCode !== 200) {
        setErrorMessage(cusProviderResponse?.outputMessage)
      }
    } catch (error) {
      /* empty */
    } finally {
      setCustomLoading(false)
      window.location.reload()
    }
  }

  const isCustomerStreamingPassesActive = isStreamingPassesActive(customer)
  
  const isFetching = false

  if (isFetching || customLoading) {
    return <Loading mt="32px" size="lg" />
  }

  return (
    <Flex w="100%" h="100%" mb={8} direction="column" align="center">
      <Stack w="100%" direction="column" spacing={4} maxW={{ base: '100%', xl: '860px' }}>
        <Flex
          w="100%"
          borderRadius="5px"
          px={{ base: 4, lg: 0 }}
          py={{ base: 4, lg: 2 }}
          backgroundColor={theme.baseColors.background.default}
        >
          <Heading
            data-testid="welcomeMessage"
            as={isLargeViewport ? 'h1' : 'h2'}
            text={`👋 Welcome, ${user?.given_name}`}
          />
        </Flex>
        <Grid
          templateColumns={isLargeViewport && matchesForStreams?.length > 2 ? '1.5fr 1fr' : '1fr'}
          gap={{ base: '4px', lg: '30px' }}
        >
          {streamMatchPairs?.length > 0 && (
            <Stack flex={1} mt="20px" spacing={isLargeViewport ? '20px' : '10px'}>
              <Heading as="h2" text="Recent Video Creations" />
              <Stack alignItems="center" spacing="8px">
                {streamMatchPairs?.map(pair => (
                  <StreamCard key={`${pair.date}-${pair.stream.streamGuid}`} pair={pair} isActionable />
                ))}
              </Stack>
            </Stack>
          )}
          
          <Stack flex={1} w="100%" direction="column" mt="20px" spacing={isLargeViewport ? '20px' : '10px'}>
          <CardAction
              icon={{
                source: ['far', 'image'],
                background: theme.colors.purple['50'],
                color: theme.baseColors.secondary.dark,
              }}
              size={isLargeViewport ? 'lg' : 'sm'}
              title="Create Images"
              message="Generate images and use statically or to create videos."
              link={{ url: '/image-generation' }}
              isDisabled={false}
            />
            <CardAction
              icon={{
                source: ['far', 'play'],
                background: theme.colors.purple['50'],
                color: theme.baseColors.secondary.dark,
              }}
              size={isLargeViewport ? 'lg' : 'sm'}
              title="Create New Video"
              message="Generate a new AI video using a specific style and script."
              link={{ url: '/create-video' }}
              isDisabled={false}
            />
            <CardAction
              icon={{
                source: ['far', 'video'],
                background: theme.colors.purple['50'],
                color: theme.baseColors.secondary.dark,
              }}
              size={isLargeViewport ? 'lg' : 'sm'}
              title="Manage Videos"
              message="View, edit, or delete your AI-generated videos."
              link={{ url: '/manage-video' }}
              isDisabled={false}
            />
            <CardAction
              icon={{
                source: ['far', 'user'],
                background: theme.colors.purple['50'],
                color: theme.baseColors.secondary.dark,
              }}
              size={isLargeViewport ? 'lg' : 'sm'}
              title="Interactive Avatar"
              message="Interactive Avatar configuration."
              link={{ url: '/interactive-avatar' }}
              isDisabled={false}
            />
          </Stack>
        </Grid>
        {isLargeViewport && errorMessage && (
          <CardInfo
            icon={{
              source: ['far', 'circle-info'],
              color: '#FFF',
            }}
            message={errorMessage}
            status="warning"
          />
        )}
      </Stack>
    </Flex>
  )
}
